"use strict";
import { LiquidityEventName } from "@uniswap/analytics-events";
import { getLiquidityEventName } from "components/Liquidity/analytics";
import { popupRegistry } from "components/Popups/registry";
import { PopupType } from "components/Popups/types";
import {
  handleApprovalTransactionStep,
  handleOnChainStep,
  handlePermitTransactionStep,
  handleSignatureStep
} from "state/sagas/transactions/utils";
import { handleAtomicSendCalls } from "state/sagas/transactions/5792";
import {
  TransactionType
} from "state/transactions/types";
import invariant from "tiny-invariant";
import { call } from "typed-redux-saga";
import { sendAnalyticsEvent } from "features/telemetry/send";
import { generateLPTransactionSteps } from "features/transactions/liquidity/steps/generateLPTransactionSteps";
import {
  LiquidityTransactionType
} from "features/transactions/liquidity/types";
import { TransactionStepType } from "features/transactions/steps/types";
import { createSaga } from "utils/saga";
import { logger } from "utilities/src/logger/logger";
import { currencyId } from "utils/currencyId";
import {
  TRANSACTION_CANCELLED_DESCRIPTION,
  TRANSACTION_CANCELLED_TITLE,
  TRANSACTION_CANCELLED_TOAST_MS,
  getReadableTransactionFailureReason,
  isUserRejectedTransactionError
} from "utils/transactionErrors";
import { deserializeLiquidityTxContext } from "../../../utils/currencyAmountSerialization";
function addLiquidityActionFailureToast(action, errorMessage) {
  const { currency0Amount, currency1Amount } = action;
  const token0Symbol = currency0Amount.currency.symbol;
  const token1Symbol = currency1Amount.currency.symbol;
  let toastKey;
  let toastContent;
  switch (action.type) {
    case LiquidityTransactionType.Create:
      toastKey = `pool-create-failed-${Date.now()}`;
      toastContent = {
        type: "error",
        title: "Pool Creation Failed",
        description: `Failed to create ${token0Symbol}/${token1Symbol} liquidity position: ${errorMessage}`
      };
      break;
    case LiquidityTransactionType.Increase:
      toastKey = `liquidity-add-failed-${Date.now()}`;
      toastContent = {
        type: "error",
        title: "Add Liquidity Failed",
        description: `Failed to add liquidity to ${token0Symbol}/${token1Symbol} position: ${errorMessage}`
      };
      break;
    case LiquidityTransactionType.Decrease:
      toastKey = `liquidity-remove-failed-${Date.now()}`;
      toastContent = {
        type: "error",
        title: "Remove Liquidity Failed",
        description: `Failed to remove liquidity from ${token0Symbol}/${token1Symbol} position: ${errorMessage}`
      };
      break;
    case LiquidityTransactionType.Collect:
      toastKey = `fees-collect-failed-${Date.now()}`;
      toastContent = {
        type: "error",
        title: "Collect Fees Failed",
        description: `Failed to collect fees from ${token0Symbol}/${token1Symbol} position: ${errorMessage}`
      };
      break;
    case LiquidityTransactionType.Migrate:
      toastKey = `position-migrate-failed-${Date.now()}`;
      toastContent = {
        type: "error",
        title: "Position Migration Failed",
        description: `Failed to migrate ${token0Symbol}/${token1Symbol} position: ${errorMessage}`
      };
      break;
    default:
      return;
  }
  popupRegistry.addPopup({ type: PopupType.LiquidityError, content: toastContent }, toastKey);
}
function* getLiquidityTxRequest(step, signature) {
  if (step.type === TransactionStepType.IncreasePositionTransaction || step.type === TransactionStepType.DecreasePositionTransaction || step.type === TransactionStepType.MigratePositionTransaction || step.type === TransactionStepType.CollectFeesTransactionStep) {
    return step.txRequest;
  }
  if (!signature) {
    throw new Error("Signature required for async increase position transaction step");
  }
  const txRequest = yield* call(step.getTxRequest, signature);
  invariant(txRequest !== void 0, "txRequest must be defined");
  return txRequest;
}
function* handlePositionTransactionStep(params) {
  const { action, step, signature, analytics } = params;
  const info = getLiquidityTransactionInfo(action);
  const txRequest = yield* call(getLiquidityTxRequest, step, signature);
  const onModification = ({ hash: hash2, data }) => {
    if (analytics) {
      sendAnalyticsEvent(LiquidityEventName.TRANSACTION_MODIFIED_IN_WALLET, {
        ...analytics,
        transaction_hash: hash2,
        expected: txRequest.data?.toString(),
        actual: data
      });
    }
  };
  const onChainStep = { ...step, txRequest };
  const hash = yield* call(handleOnChainStep, {
    ...params,
    info,
    step: onChainStep,
    shouldWaitForConfirmation: false,
    onModification
  });
  if (analytics) {
    sendAnalyticsEvent(getLiquidityEventName(onChainStep.type), {
      ...analytics,
      transaction_hash: hash
    });
  }
  popupRegistry.addPopup({ type: PopupType.Transaction, hash }, hash);
}
function* handleLiquidityTransactionBatchedStep(params) {
  const { action, step } = params;
  const info = getLiquidityTransactionInfo(action);
  try {
    const batchId = yield* handleAtomicSendCalls({
      ...params,
      info,
      step,
      shouldWaitForConfirmation: false
    });
    popupRegistry.addPopup({ type: PopupType.Transaction, hash: batchId }, batchId);
  } catch (error) {
    logger.warn(
      "liquiditySaga",
      "handleLiquidityTransactionBatchedStep",
      "Atomic batch failed, falling back to sequential execution",
      { error }
    );
    if (!step.fallbackSteps?.length) {
      throw error;
    }
    for (const fallbackStep of step.fallbackSteps) {
      switch (fallbackStep.type) {
        case TransactionStepType.TokenApprovalTransaction: {
          yield* call(handleApprovalTransactionStep, {
            account: params.account,
            step: fallbackStep,
            setCurrentStep: params.setCurrentStep
          });
          break;
        }
        case TransactionStepType.IncreasePositionTransaction:
        case TransactionStepType.DecreasePositionTransaction: {
          yield* call(handlePositionTransactionStep, {
            account: params.account,
            step: fallbackStep,
            setCurrentStep: params.setCurrentStep,
            action: params.action,
            signature: void 0,
            analytics: params.analytics
          });
          break;
        }
        default: {
          throw new Error("Unsupported fallback step provided for batched liquidity transaction");
        }
      }
    }
  }
}
function* modifyLiquidity(params) {
  const {
    account,
    setCurrentStep,
    steps,
    liquidityTxContext: { action },
    onSuccess,
    onFailure,
    analytics
  } = params;
  let signature;
  try {
    for (const step of steps) {
      switch (step.type) {
        case TransactionStepType.TokenRevocationTransaction:
        case TransactionStepType.TokenApprovalTransaction: {
          yield* call(handleApprovalTransactionStep, { account, step, setCurrentStep });
          break;
        }
        case TransactionStepType.Permit2Signature: {
          signature = yield* call(handleSignatureStep, { account, step, setCurrentStep });
          break;
        }
        case TransactionStepType.Permit2Transaction: {
          yield* call(handlePermitTransactionStep, { account, step, setCurrentStep });
          break;
        }
        case TransactionStepType.IncreasePositionTransaction:
        case TransactionStepType.IncreasePositionTransactionAsync:
        case TransactionStepType.DecreasePositionTransaction:
        case TransactionStepType.MigratePositionTransaction:
        case TransactionStepType.MigratePositionTransactionAsync:
        case TransactionStepType.CollectFeesTransactionStep:
          yield* call(handlePositionTransactionStep, { account, step, setCurrentStep, action, signature, analytics });
          break;
        case TransactionStepType.IncreasePositionTransactionBatched:
          yield* call(handleLiquidityTransactionBatchedStep, { account, step, setCurrentStep, action, analytics });
          break;
        case TransactionStepType.DecreasePositionTransactionBatched:
          yield* call(handleLiquidityTransactionBatchedStep, { account, step, setCurrentStep, action, analytics });
          break;
        default: {
          throw new Error("Unexpected step type");
        }
      }
    }
  } catch (e) {
    if (isUserRejectedTransactionError(e)) {
      popupRegistry.addPopup(
        {
          type: PopupType.LiquidityError,
          content: {
            type: "error",
            title: TRANSACTION_CANCELLED_TITLE,
            description: TRANSACTION_CANCELLED_DESCRIPTION
          }
        },
        `liquidity-cancelled-${Date.now()}`,
        TRANSACTION_CANCELLED_TOAST_MS
      );
      onFailure();
      return;
    }
    const cause = e instanceof Error && e.cause;
    logger.error(e, { tags: { file: "liquiditySaga", function: "modifyLiquidity" }, extra: { tradingApiError: cause } });
    addLiquidityActionFailureToast(action, getReadableTransactionFailureReason(e));
    onFailure();
    return;
  }
  yield* call(onSuccess);
}
function* liquidity(params) {
  const { liquidityTxContext: serializedLiquidityTxContext, startChainId, selectChain, onFailure, supportsEip5792 = false } = params;
  const liquidityTxContext = deserializeLiquidityTxContext(serializedLiquidityTxContext);
  const steps = yield* call(generateLPTransactionSteps, liquidityTxContext, { supportsEip5792 });
  params.setSteps(steps);
  const token0ChainId = liquidityTxContext.action.currency0Amount.currency.chainId;
  const token1ChainId = liquidityTxContext.action.currency1Amount.currency.chainId;
  if (token0ChainId !== token1ChainId) {
    logger.error("Tokens must be on the same chain", {
      tags: { file: "liquiditySaga", function: "liquidity" }
    });
    onFailure();
    return void 0;
  }
  if (token0ChainId !== startChainId) {
    const chainSwitched = yield* call(selectChain, token0ChainId);
    if (!chainSwitched) {
      onFailure();
      return void 0;
    }
  }
  return yield* modifyLiquidity({
    ...params,
    // Pass the deserialized context so downstream consumers (toast, transaction
    // info, on-chain step) see real `CurrencyAmount` instances with working
    // `.quotient` getters, not the serialized `{ numerator, denominator }` shape.
    liquidityTxContext,
    steps
  });
}
export const liquiditySaga = createSaga(liquidity, "liquiditySaga");
function getLiquidityTransactionInfo(action) {
  let type;
  switch (action.type) {
    case LiquidityTransactionType.Create:
      type = TransactionType.CREATE_POSITION;
      break;
    case LiquidityTransactionType.Increase:
      type = TransactionType.INCREASE_LIQUIDITY;
      break;
    case LiquidityTransactionType.Decrease:
      type = TransactionType.DECREASE_LIQUIDITY;
      break;
    case LiquidityTransactionType.Migrate:
      type = TransactionType.MIGRATE_LIQUIDITY_V3_TO_V4;
      break;
    case LiquidityTransactionType.Collect:
      type = TransactionType.COLLECT_FEES;
  }
  const {
    currency0Amount: { currency: currency0, quotient: quotient0 },
    currency1Amount: { currency: currency1, quotient: quotient1 }
  } = action;
  function quotientToRawString(quotient) {
    if (quotient === void 0 || quotient === null) {
      return "0";
    }
    if (typeof quotient === "bigint" || typeof quotient === "number") {
      return String(quotient);
    }
    if (typeof quotient === "string") {
      return /^-?\d+$/.test(quotient) ? quotient : "0";
    }
    if (typeof quotient.toString === "function") {
      const stringified = quotient.toString();
      if (/^-?\d+$/.test(stringified)) {
        return stringified;
      }
    }
    return "0";
  }
  return {
    type,
    token0CurrencyId: currencyId(currency0),
    token1CurrencyId: currencyId(currency1),
    token0CurrencyAmountRaw: quotientToRawString(quotient0),
    token1CurrencyAmountRaw: quotientToRawString(quotient1)
  };
}
